/**
 * Alpha video playback helpers.
 * - WebM: seamless loop (seek before end) to avoid VP9 alpha flicker
 * - MP4 / HEVC / MOV: native loop only (no JS seek — mobile opaque + legacy alpha)
 * - defer priority: load/play only when near viewport
 * - hidden desktop heroes: wait until visible before loading
 */
(function () {
  var utils = window.SigmaAlphaVideo || {};
  var isAlphaVideoVisible = utils.isAlphaVideoVisible || function () {
    return true;
  };
  var startAlphaVideo = utils.startAlphaVideo || function (video) {
    video.preload = 'auto';
    video.load();
    video.play().catch(function () {});
  };
  var tryPlay = utils.tryPlay || function (video) {
    video.muted = true;
    video.playsInline = true;
    if (video.paused) video.play().catch(function () {});
  };

  var LOOP_LEAD = 0.08;
  var LOOP_START = 0.04;
  var SELECTOR = 'video[data-alpha-video]';
  var MOBILE_MQ = window.matchMedia('(max-width: 61.8125rem)');

  function inferAlphaFormat(video) {
    if (video.dataset.alphaFormat) return video.dataset.alphaFormat;
    var source = video.querySelector('source');
    if (!source) return '';
    var hint = (source.type || '') + ' ' + (source.src || '');
    if (/webm/i.test(hint)) return 'webm';
    if (/\.mp4/i.test(hint) || (video.dataset.alphaFormat === 'mp4')) return 'mp4';
    return 'hevc';
  }

  function usesNativeLoop(format) {
    return format === 'hevc' || format === 'mp4';
  }

  function bindSeamlessLoop(video) {
    if (video.dataset.seamlessBound === 'true') return;
    video.dataset.seamlessBound = 'true';

    var duration = NaN;
    var seeking = false;

    var applyLoop = function () {
      if (seeking || !Number.isFinite(duration) || duration <= LOOP_LEAD + LOOP_START) {
        return;
      }
      if (video.currentTime >= duration - LOOP_LEAD) {
        seeking = true;
        video.currentTime = LOOP_START;
      }
    };

    video.addEventListener(
      'loadedmetadata',
      function () {
        duration = video.duration;
        tryPlay(video);
      },
      { passive: true }
    );

    video.addEventListener(
      'durationchange',
      function () {
        duration = video.duration;
      },
      { passive: true }
    );

    video.addEventListener('timeupdate', applyLoop, { passive: true });

    video.addEventListener('seeked', function () {
      seeking = false;
      tryPlay(video);
    });

    if (video.readyState >= 1) {
      duration = video.duration;
    }
  }

  function bindNativeLoopPlay(video) {
    if (video.dataset.nativeLoopBound === 'true') return;
    video.dataset.nativeLoopBound = 'true';

    if (video.readyState >= 1) {
      tryPlay(video);
    } else {
      video.addEventListener(
        'loadedmetadata',
        function () {
          tryPlay(video);
        },
        { once: true, passive: true }
      );
    }
  }

  function bindPlayback(video) {
    var format = inferAlphaFormat(video);
    if (format === 'webm') {
      bindSeamlessLoop(video);
    } else if (usesNativeLoop(format)) {
      bindNativeLoopPlay(video);
    }
  }

  function wakeVisibleHero(video) {
    if (video.dataset.alphaWaitVisible !== 'true') return;
    if (!isAlphaVideoVisible(video)) return;

    delete video.dataset.alphaWaitVisible;

    if (video.dataset.alphaPriority === 'hero' && video.dataset.alphaLoaded !== 'true') {
      var source = video.querySelector('source');
      var fmt = inferAlphaFormat(video);
      if (source && usesNativeLoop(fmt) && utils.preloadMobileOnce) {
        utils.preloadMobileOnce(source.src);
      } else if (source && usesNativeLoop(fmt) && utils.preloadHevcOnce) {
        utils.preloadHevcOnce(source.src);
      }
      startAlphaVideo(video);
    }

    bindPlayback(video);
  }

  function bindVisibilityWake(video) {
    if (video.dataset.visibilityWakeBound === 'true') return;
    if (video.dataset.alphaWaitVisible !== 'true' && isAlphaVideoVisible(video)) return;

    video.dataset.visibilityWakeBound = 'true';

    var check = function () {
      wakeVisibleHero(video);
    };

    if (typeof ResizeObserver !== 'undefined') {
      var ro = new ResizeObserver(check);
      ro.observe(video);
      if (video.parentElement) ro.observe(video.parentElement);
    }

    if (typeof MOBILE_MQ.addEventListener === 'function') {
      MOBILE_MQ.addEventListener('change', check);
    } else if (typeof MOBILE_MQ.addListener === 'function') {
      MOBILE_MQ.addListener(check);
    }

    window.addEventListener('resize', check, { passive: true });
    check();
  }

  function startDeferredLoad(video) {
    if (!isAlphaVideoVisible(video)) return;

    if (video.dataset.alphaLoaded === 'true') {
      tryPlay(video);
      return;
    }

    startAlphaVideo(video);
    bindPlayback(video);
  }

  function bindDeferredLoad(video) {
    if (video.dataset.deferBound === 'true') return;
    if (video.dataset.alphaPriority !== 'defer') return;

    if (!('IntersectionObserver' in window)) {
      startDeferredLoad(video);
      return;
    }

    video.dataset.deferBound = 'true';

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            startDeferredLoad(video);
          } else if (video.dataset.alphaLoaded === 'true') {
            video.pause();
          }
        });
      },
      { rootMargin: '80px', threshold: 0.08 }
    );

    observer.observe(video);
  }

  function retryVisiblePausedVideos() {
    document.querySelectorAll(SELECTOR).forEach(function (video) {
      if (!isAlphaVideoVisible(video)) return;
      if (video.dataset.alphaWaitVisible === 'true') {
        wakeVisibleHero(video);
      }
      if (video.paused && video.dataset.alphaLoaded === 'true') {
        tryPlay(video);
      }
    });
  }

  function initVideo(video) {
    if (video.dataset.alphaWaitVisible === 'true' || !isAlphaVideoVisible(video)) {
      bindVisibilityWake(video);
    }

    if (video.dataset.alphaPriority === 'defer') {
      bindDeferredLoad(video);
      return;
    }

    if (video.dataset.alphaLoaded === 'true') {
      bindPlayback(video);
      return;
    }

    bindPlayback(video);
  }

  function init() {
    var initFromInline = utils.initFromInline;
    document.querySelectorAll(SELECTOR).forEach(function (video) {
      if (initFromInline) initFromInline(video);
      initVideo(video);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') retryVisiblePausedVideos();
  });

  window.addEventListener('pageshow', retryVisiblePausedVideos);

  document.addEventListener('shopify:section:load', init);
})();
